// Define API base URL with proper port
// This should match the backend server address from start.sh
const API_BASE_URL = (() => {
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:8001/api/v1';
  } else if (window.location.hostname === 'staging.maxel.ai') {
    return '/api';
  } else {
    return '/api/v1';
  }
})();
import { auth } from './firebase';
import { getAuth } from 'firebase/auth';
import axios from 'axios';
import { getAnonymousSessionId, isAnonymousUser } from '../utils/anonymousSession';

// Helper function to get auth token
const getAuthToken = async (): Promise<string | null> => {
  const user = auth.currentUser;
  if (user) {
    return user.getIdToken();
  }
  return null;
};

// Add auth headers to fetch options
export const addAuthHeaders = async (options = {}): Promise<RequestInit> => {
  const token = await getAuthToken();
  const headers: HeadersInit = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    console.log('Adding auth token to request:', token.substring(0, 10) + '...');
  } else {
    // For anonymous users, add session ID header
    const anonymousSessionId = getAnonymousSessionId();
    headers['X-Anonymous-Session-Id'] = anonymousSessionId;
    console.log('Adding anonymous session ID to request:', anonymousSessionId.substring(0, 15) + '...');
  }
  
  // Special case for FormData - don't set Content-Type
  if ((options as any)?.body instanceof FormData) {
    delete headers['Content-Type'];
  }
  
  const finalOptions = {
    ...options,
    headers: {
      ...headers,
      ...(options as any)?.headers
    },
    credentials: 'include' as RequestCredentials
  };
  
  return finalOptions;
};

interface FileUploadResponse {
  id: string;
  original_filename: string;
  original_size_bytes: number;
  detected_mime_type: string;
  upload_time: string;
  status: string;
  glb_path: string;
  three_mf_path: string;
  preview_url?: string;
}

interface FileList {
  files: FileUploadResponse[];
}

interface OrientationSettings {
  printer: string;
  nozzle: string;
  material: string;
  // packer?: string; // Removed packer option
}

interface OrientationResult {
  overall_success: boolean;
  results: Record<string, {
    success: boolean;
    file_id?: string;
    message?: string;
    error?: string;
  }>;
  merged_file?: {
    id: string;
    filename: string;
    download_url: string;
    model_count: number;
  };
}

export interface Plan {
  type: 'free' | 'pro' | 'studio';
  name: string;
  features: {
    credits_monthly: number;
    storage_bytes: number;
    gcodes_monthly: number;
    price_usd: number;
  };
}

export interface UserPlanInfo {
  user_id: string;
  current_plan: Plan;
  subscription?: {
    id: string;
    status: string;
    current_period_end: string;
  };
  credits: {
    credits_used: number;
    credits_remaining: number;
    period_end: string;
  };
  storage_used_bytes: number;
  storage_limit_bytes: number;
}

// Institution interfaces
export interface Institution {
  id: string;
  name: string;
  subdomain: string;
  custom_url: string;
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  allowed_email_domains: string[];
  is_active: boolean;
  allow_signups?: boolean;
  created_at: string;
  updated_at: string;
}

// License interfaces
export interface LicenseFeatures {
  max_users: number;
  hours_per_user_monthly?: number | null;
  total_hours_monthly?: number | null;
  price_usd_monthly: number;
  price_usd_yearly?: number | null;
  teacher_accounts_included: number;
  admin_dashboard_access: boolean;
  white_label_branding: boolean;
  advanced_analytics: boolean;
  priority_support: boolean;
  bulk_user_import: boolean;
  api_access: boolean;
}

export interface LicenseDefinition {
  id: string;
  name: string;
  description: string;
  billing_model: 'hours_based' | 'pay_per_print';
  features: LicenseFeatures;
  is_popular: boolean;
  is_active: boolean;
  display_order: number;
  target_audience: string;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface LicenseCreate {
  name: string;
  description: string;
  billing_model: 'hours_based' | 'pay_per_print';
  features: LicenseFeatures;
  is_popular?: boolean;
  display_order?: number;
  target_audience: string;
}

export interface InstitutionPublicConfig {
  id: string;
  name: string;
  subdomain: string;
  custom_url: string;
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  allow_signups?: boolean;
}

export interface InstitutionDetectionInfo {
  institution?: InstitutionPublicConfig;
  detection_method: 'subdomain' | 'query_param' | 'url_path' | 'none';
  subdomain_extracted?: string;
  query_param_value?: string;
  url_path_extracted?: string;
  environment: 'development' | 'staging' | 'production';
  current_url: string;
}

export const api = {
  auth: {
    // Get current user with role information
    me: async () => {
      const options = await addAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/auth/me`, options);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || 'Failed to get user info');
      }
      return response.json();
    },

    // Get user role and redirect URL
    getRole: async () => {
      const options = await addAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/auth/role`, options);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || 'Failed to get user role');
      }
      return response.json();
    },

    // Check platform access
    checkAccess: async (platform: string) => {
      const options = await addAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/auth/check-access/${platform}`, options);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || 'Failed to check access');
      }
      return response.json();
    },

    // Assign role (Super Admin only)
    assignRole: async (targetUserUid: string, role: string, institutionId?: string) => {
      const options = await addAuthHeaders({
        method: 'POST',
        body: JSON.stringify({
          role,
          institution_id: institutionId
        })
      });
      const response = await fetch(`${API_BASE_URL}/auth/assign-role?target_user_uid=${targetUserUid}`, options);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || 'Failed to assign role');
      }
      return response.json();
    },

    // Assign role by email (Super Admin only)
    assignRoleByEmail: async (email: string, role: string, institutionId?: string) => {
      const options = await addAuthHeaders({
        method: 'POST',
        body: JSON.stringify({
          role,
          institution_id: institutionId
        })
      });
      const response = await fetch(`${API_BASE_URL}/auth/assign-role-by-email?email=${encodeURIComponent(email)}`, options);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || 'Failed to assign role');
      }
      return response.json();
    },

    // Get users
    getUsers: async (role?: string) => {
      const params = role ? `?role=${role}` : '';
      const options = await addAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/auth/users${params}`, options);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || 'Failed to get users');
      }
      return response.json();
    },

    // Validate email domain for institutional assignment
    validateEmailDomain: async (email: string) => {
      const options = await addAuthHeaders({
        method: 'POST',
        body: JSON.stringify({ email })
      });
      const response = await fetch(`${API_BASE_URL}/auth/validate-email-domain`, options);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || 'Failed to validate email domain');
      }
      return response.json();
    },

    // Register and validate user with institutional assignment
    registerValidate: async (email: string) => {
      const options = await addAuthHeaders({
        method: 'POST',
        body: JSON.stringify({ email })
      });
      const response = await fetch(`${API_BASE_URL}/auth/register-validate`, options);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || 'Failed to validate registration');
      }
      return response.json();
    },

    // Batch assign users by email domain
    batchAssignUsers: async () => {
      const options = await addAuthHeaders({
        method: 'POST'
      });
      const response = await fetch(`${API_BASE_URL}/auth/batch-assign-users`, options);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || 'Failed to batch assign users');
      }
      return response.json();
    }
  },

  health: {
    check: async () => {
      try {
        console.log('Checking health endpoint:', `${API_BASE_URL}/health`);
        const response = await fetch(`${API_BASE_URL}/health`);
        if (!response.ok) {
          throw new Error(`Health check failed with status ${response.status}`);
        }
        return response.json();
      } catch (error) {
        console.error('Health check error:', error);
        throw error;
      }
    }
  },
  
  projects: {
    // Until backend project router is implemented, this uses files API
    delete: async (projectName: string, deleteFiles: boolean = true): Promise<any> => {
      console.log(`[DEBUG] Deleting project: ${projectName}, delete files: ${deleteFiles}`);
      
      try {
        // First, get all files for this project
        const options = await addAuthHeaders();
        const filesResponse = await fetch(`${API_BASE_URL}/files/by-project/${encodeURIComponent(projectName)}`, options);
        
        if (!filesResponse.ok) {
          const errorData = await filesResponse.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(`Failed to get project files: ${errorData.detail || filesResponse.statusText}`);
        }
        
        const filesData = await filesResponse.json();
        const files = filesData.files || [];
        
        // If we want to delete files, delete each one
        if (deleteFiles && files.length > 0) {
          const deleteOptions = await addAuthHeaders({
            method: 'DELETE',
          });
          
          // Delete each file
          for (const file of files) {
            console.log(`[DEBUG] Deleting file: ${file.id}`);
            const deleteResponse = await fetch(`${API_BASE_URL}/files/${file.id}`, deleteOptions);
            
            if (!deleteResponse.ok) {
              console.warn(`[DEBUG] Failed to delete file ${file.id}: ${deleteResponse.status} ${deleteResponse.statusText}`);
            }
          }
        }
        
        return { status: "success", message: "Project files deleted" };
      } catch (error) {
        console.error('[DEBUG] Error in deleteProject:', error);
        throw error;
      }
    },
    
    // Rename a project by using the backend API
    rename: async (oldName: string, newName: string): Promise<any> => {
      console.log(`[DEBUG] Renaming project from "${oldName}" to "${newName}"`);
      
      try {
        const options = await addAuthHeaders({
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            new_name: newName
          })
        });
        
        const response = await fetch(`${API_BASE_URL}/projects/rename/${encodeURIComponent(oldName)}`, options);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(`Failed to rename project: ${errorData.detail || response.statusText}`);
        }
        
        const result = await response.json();
        console.log(`[DEBUG] Successfully renamed project from "${oldName}" to "${newName}"`);
        
        return result;
      } catch (error) {
        console.error('[DEBUG] Error in renameProject:', error);
        throw error;
      }
    }
  },
  
  files: {
    upload: async (file: File, projectName: string): Promise<FileUploadResponse> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('project_name', projectName);
      
      const options = await addAuthHeaders({
        method: 'POST',
        body: formData,
      });
      
      const response = await fetch(`${API_BASE_URL}/files/upload`, options);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }
      
      return response.json();
    },
    
    list: async (): Promise<FileList> => {
      console.log('Listing files - checking authentication...');
      const user = auth.currentUser;
      if (!user) {
        console.error('User not authenticated when trying to list files');
        throw new Error('Authentication required to list files');
      }
      
      console.log('User authenticated:', user.uid);
      const options = await addAuthHeaders();
      
      try {
        console.log('Fetching files from:', `${API_BASE_URL}/files/list`);
        const response = await fetch(`${API_BASE_URL}/files/list`, options);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Failed to fetch files:', response.status, errorText);
          throw new Error(`Failed to fetch files: ${response.status} - ${errorText}`);
        }
        
        return response.json();
      } catch (error) {
        console.error('Error in list files API call:', error);
        throw error;
      }
    },
    
    // Add method to get download URL for a file
    getDownloadUrl: async (fileId: string, fileType: 'original'|'glb'|'three_mf'): Promise<string> => {
      console.log(`[DEBUG] Requesting download URL for fileId: ${fileId}, type: ${fileType}`);
      const options = await addAuthHeaders();
      try {
        const response = await fetch(`${API_BASE_URL}/files/${fileId}/url/${fileType}`, options);
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
          console.error(`[DEBUG] Error getting file URL: ${JSON.stringify(errorData)}`);
          throw new Error(`Failed to get file URL: ${errorData.detail || response.statusText}`);
        }
        const data = await response.json();
        console.log(`[DEBUG] Successfully got URL for ${fileId}`);
        return data.url;
      } catch (error) {
        console.error(`[DEBUG] Exception getting URL for ${fileId}:`, error);
        throw error;
      }
    },
    
    // Get files by project name
    getFilesByProject: async (projectName: string): Promise<FileList> => {
      const options = await addAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/files/by-project/${encodeURIComponent(projectName)}`, options);
      if (!response.ok) throw new Error('Failed to fetch project files');
      return response.json();
    },

    // Delete a file by ID
    delete: async (fileId: string): Promise<any> => {
      console.log(`[DEBUG] Deleting file: ${fileId}`);
      const options = await addAuthHeaders({
        method: 'DELETE'
      });
      
      const response = await fetch(`${API_BASE_URL}/files/${fileId}`, options);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(`Failed to delete file: ${errorData.detail || response.statusText}`);
      }
      
      const data = await response.json();
      console.log(`[DEBUG] Successfully deleted file: ${fileId}`);
      return data;
    }
  },
  
  slicing: {
    process: async (fileId: string) => {
      const options = await addAuthHeaders({
        method: 'POST'
      });
      
      const response = await fetch(`${API_BASE_URL}/slicing/process/${fileId}`, options);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Processing failed');
      }
      
      return response.json();
    }
  },
  
  orientation: {
    process: async (fileIds: string[], settings: OrientationSettings, mergeFiles: boolean = true): Promise<OrientationResult> => {
      const options = await addAuthHeaders({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          file_ids: fileIds,
          settings,
          merge_files: mergeFiles
        })
      });
      
      const response = await fetch(`${API_BASE_URL}/orientation/process`, options);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Auto-orientation failed');
      }
      
      return response.json();
    }
  },
  
  gcode: {
    generate: async (fileIds: string[], settings: OrientationSettings): Promise<any> => {
      const options = await addAuthHeaders({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          file_ids: fileIds,
          settings
        })
      });
      
      const response = await fetch(`${API_BASE_URL}/gcode/generate`, options);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'G-code generation failed');
      }
      
      return response.json();
    },
    
    packModels: async (fileIds: string[], settings: OrientationSettings): Promise<any> => {
      const options = await addAuthHeaders({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          file_ids: fileIds,
          settings
        })
      });
      
      const response = await fetch(`${API_BASE_URL}/gcode/pack-models`, options);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Model packing failed');
      }
      
      return response.json();
    },
    
    generateArrangedGcode: async (models: Array<{ id: string, duplicates: number, scale?: number }>, settings: OrientationSettings, userIntent?: string, customParameters?: Array<{parameter: string, value: string, reasoning: string}>): Promise<any> => {
      const options = await addAuthHeaders({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          models: models,
          settings,
          user_intent: userIntent || 'balanced',
          custom_parameters: customParameters
        })
      });
      
      const response = await fetch(`${API_BASE_URL}/gcode/generate-arranged`, options);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Arranged G-code generation failed');
      }
      
      const data = await response.json();
      console.log(`[DEBUG] Arranged G-code response:`, data);
      
      // Check if this is an immediate response with a job_id (async processing)
      if (data.job_id && data.status === 'queued') {
        console.log(`[DEBUG] Job submitted for async processing: ${data.job_id}`);
        // Poll for job completion
        const result = await api.jobs.waitForCompletion(data.job_id);
        console.log(`[DEBUG] Job completed with result:`, result);
        return result;
      }
      
      // Return the updated format with gcode_files instead of zip
      return {
        success: data.success,
        gcode_files: data.gcode_files,
        plate_count: data.plate_count,
        timestamp_folder: data.timestamp_folder,
        error: data.error
      };
    },
    
    // Cancel ongoing packing operation
    cancelPacking: async (): Promise<any> => {
      const options = await addAuthHeaders({
        method: 'POST'
      });
      
      const response = await fetch(`${API_BASE_URL}/gcode/cancel-packing`, options);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to cancel packing operation');
      }
      
      return response.json();
    },
    
    // Check status of packing operation
    checkPackingStatus: async (): Promise<any> => {
      const options = await addAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/gcode/packing-status`, options);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to check packing status');
      }
      
      return response.json();
    },
    
    /**
     * Generate new G-code for a list of files
     */
    generateGCode: async (fileIds: string[], settings: OrientationSettings): Promise<{ 
      success: boolean; 
      gcodeFiles?: Array<{
        filename: string;
        plate_number: number;
        size: number;
        download_url: string;
        storage_path: string;
      }>;
      error?: string;
      plateCount?: number;
      timestampFolder?: string;
    }> => {
      try {
        console.log(`[DEBUG] Generating G-code for files:`, fileIds);
        
        const options = await addAuthHeaders({
          method: 'POST',
          body: JSON.stringify({
            file_ids: fileIds,
            settings: {
              printer: settings.printer,
              nozzle: settings.nozzle,
              material: settings.material
            }
          })
        });
        
        const response = await fetch(`${API_BASE_URL}/gcode/generate`, options);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(`Failed to generate G-code: ${errorData.detail || response.statusText}`);
        }
        
        const data = await response.json();
        console.log(`[DEBUG] G-code generation response:`, data);
        
        if (!data.success) {
          return {
            success: false,
            error: data.error || 'Failed to generate G-code'
          };
        }
        
        return {
          success: true,
          gcodeFiles: data.gcode_files,
          plateCount: data.plate_count,
          timestampFolder: data.timestamp_folder
        };
      } catch (error) {
        console.error(`[DEBUG] Error generating G-code:`, error);
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        };
      }
    },
    
    /**
     * Get the latest gcode files for a project
     */
    getProjectGCodeFiles: async (projectName: string): Promise<{
      success: boolean;
      gcode_folders?: Array<{
        folder_name: string;
        readable_date: string;
        created_at: string | null;
        files: Array<{
          filename: string;
          plate_number: number;
          size: number;
          download_url: string;
          storage_path: string;
        }>;
      }>;
      gcode_files?: Array<{
        filename: string;
        plate_number: number;
        size: number;
        download_url: string;
        storage_path: string;
      }>;
      error?: string;
    }> => {
      try {
        const options = await addAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/gcode/project/${encodeURIComponent(projectName)}`, options);
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to fetch GCode files');
        }
        
        const data = await response.json();
        console.log(`[DEBUG] Retrieved GCode files for project ${projectName}:`, data);
        
        if (!data.success) {
          return {
            success: false,
            error: data.error || 'Failed to retrieve GCode files'
          };
        }
        
        // Return both formats for backward compatibility
        return {
          success: true,
          gcode_folders: data.gcode_folders || [],
          gcode_files: data.gcode_files || []
        };
      } catch (error) {
        console.error(`[DEBUG] Error fetching GCode files:`, error);
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        };
      }
    },
    
    /**
     * Delete a GCode folder and all its files
     */
    deleteGCodeFolder: async (projectName: string, folderName: string): Promise<{
      success: boolean;
      deleted_files?: string[];
      error?: string;
    }> => {
      try {
        const options = await addAuthHeaders({
          method: 'DELETE'
        });
        
        const response = await fetch(`${API_BASE_URL}/gcode/folder/${encodeURIComponent(projectName)}/${encodeURIComponent(folderName)}`, options);
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to delete GCode folder');
        }
        
        const data = await response.json();
        console.log(`[DEBUG] Deleted GCode folder ${folderName} from project ${projectName}:`, data);
        
        return {
          success: data.success,
          deleted_files: data.deleted_files
        };
      } catch (error) {
        console.error(`[DEBUG] Error deleting GCode folder:`, error);
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        };
      }
    },
    
    /**
     * Deduct credits when G-code files are accessed/viewed
     */
    deductCreditsForGcode: async (plateCount: number, jobId?: string): Promise<any> => {
      const options = await addAuthHeaders({
        method: 'POST',
        body: JSON.stringify({ 
          plate_count: plateCount,
          job_id: jobId || '',
          description: `G-code access - ${plateCount} plate${plateCount > 1 ? 's' : ''}`
        })
      });
      
      const response = await fetch(`${API_BASE_URL}/subscriptions/deduct-credits`, options);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to deduct credits');
      }
      
      return response.json();
    },

    /**
     * Get bed dimensions from G-code metadata instead of parsing G-code content
     */
    getBedDimensionsFromMetadata: async (storagePath: string): Promise<{ width: number; height: number; source: string }> => {
      const response = await axios.get(`${API_BASE_URL}/gcode/bed-dimensions-from-metadata?storage_path=${encodeURIComponent(storagePath)}`);
      return response.data;
    }
  },
  
  printers: {
    getManufacturers: () => axios.get(`${API_BASE_URL}/printers/manufacturers`).then(response => response.data),
    getBedDimensions: async (manufacturer: string, model: string): Promise<{ width: number; height: number }> => {
      const response = await axios.get(`${API_BASE_URL}/printers/bed-dimensions/${encodeURIComponent(manufacturer)}/${encodeURIComponent(model)}`);
      return response.data;
    }
  },


  
  // Save and load favorites from localStorage
  favorites: {
    save: (favorites: string[]): void => {
      try {
        if (favorites && favorites.length > 0) {
          localStorage.setItem('maxel_project_favorites', JSON.stringify(favorites));
          console.log('[INFO] Saved favorites to localStorage:', favorites);
        } else {
          localStorage.removeItem('maxel_project_favorites');
          console.log('[INFO] Removed favorites from localStorage (empty list)');
        }
      } catch (e) {
        console.error('[ERROR] Failed to save favorites to localStorage:', e);
      }
    },
    
    load: (): string[] => {
      try {
        const savedData = localStorage.getItem('maxel_project_favorites');
        if (savedData) {
          const parsed = JSON.parse(savedData);
          if (Array.isArray(parsed)) {
            console.log('[INFO] Loaded favorites from localStorage:', parsed);
            return parsed;
          }
        }
      } catch (e) {
        console.error('[ERROR] Failed to load favorites from localStorage:', e);
      }
      return [];
    },
    
    toggle: (favorites: string[], projectName: string): string[] => {
      let newFavorites;
      if (favorites.includes(projectName)) {
        newFavorites = favorites.filter(name => name !== projectName);
        console.log(`[INFO] Removing "${projectName}" from favorites`);
      } else {
        newFavorites = [...favorites, projectName];
        console.log(`[INFO] Adding "${projectName}" to favorites`);
      }
      
      // Save immediately
      api.favorites.save(newFavorites);
      return newFavorites;
    },
    
    sync: (currentFavorites: string[], projectNames: string[]): string[] => {
      // Filter favorites to only include projects that still exist
      const validFavorites = currentFavorites.filter(fav => projectNames.includes(fav));
      
      // If favorites changed, update localStorage
      if (validFavorites.length !== currentFavorites.length) {
        console.log('[INFO] Syncing favorites with available projects');
        console.log('[INFO] Before:', currentFavorites);
        console.log('[INFO] After:', validFavorites);
        api.favorites.save(validFavorites);
      }
      
      return validFavorites;
    },
    
    updateProjectName: (favorites: string[], oldName: string, newName: string): string[] => {
      if (!favorites.includes(oldName)) {
        return favorites; // No change needed
      }
      
      const newFavorites = favorites.map(name => 
        name === oldName ? newName : name
      );
      
      console.log(`[INFO] Updating favorite project name from "${oldName}" to "${newName}"`);
      api.favorites.save(newFavorites);
      return newFavorites;
    }
  },
  
  jobs: {
    /**
     * Get a job by ID
     */
    getJob: async (jobId: string): Promise<any> => {
      const options = await addAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/jobs/${jobId}`, options);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to get job');
      }
      
      return response.json();
    },
    
    /**
     * List jobs for the current user
     */
    listJobs: async (status?: string, type?: string, limit: number = 20, offset: number = 0): Promise<any> => {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (type) params.append('type', type);
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
      
      const options = await addAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/jobs?${params.toString()}`, options);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to list jobs');
      }
      
      return response.json();
    },
    
    /**
     * Wait for a job to complete and return the result
     */
    waitForCompletion: async (jobId: string): Promise<any> => {
      const maxWaitTime = 5 * 60 * 1000; // 5 minutes
      const pollInterval = 2000; // 2 seconds
      const startTime = Date.now();
      
      while (Date.now() - startTime < maxWaitTime) {
        try {
          const job = await api.jobs.getJob(jobId);
          
          if (job.status === 'completed') {
            console.log(`[DEBUG] Job ${jobId} completed successfully`);
            return job.result || { success: true, job_completed: true };
          } else if (job.status === 'failed') {
            console.log(`[DEBUG] Job ${jobId} failed: ${job.error}`);
            
            // Check if the job result contains structured upgrade information
            if (job.result && typeof job.result === 'object' && job.result.upgrade_required) {
              console.log(`[DEBUG] Job failed with upgrade information:`, job.result);
              // Return the structured response instead of throwing an error
              return job.result;
            }
            
            throw new Error(job.error || 'Job failed');
          }
          
          // Job is still processing, wait and poll again
          await new Promise(resolve => setTimeout(resolve, pollInterval));
        } catch (error) {
          console.error(`[DEBUG] Error polling job ${jobId}:`, error);
          throw error;
        }
      }
      
      throw new Error('Job timeout: G-code generation took too long');
    }
  },

  storage: {
    /**
     * Get current storage usage for the authenticated user
     */
    getUsage: async (): Promise<{
      success: boolean;
      quota?: {
        limit_bytes: number;
        used_bytes: number;
        available_bytes: number;
        percentage_used: number;
      };
      error?: string;
    }> => {
      try {
        const options = await addAuthHeaders();
        
        const response = await fetch(`${API_BASE_URL}/storage/usage`, options);
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to get storage usage');
        }
        
        const data = await response.json();
        console.log('[DEBUG] Storage usage response:', data);
        
        return data;
      } catch (error) {
        console.error('[DEBUG] Error getting storage usage:', error);
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        };
      }
    },

    /**
     * Force recalculation of storage usage
     */
    recalculate: async (): Promise<{
      success: boolean;
      quota?: {
        limit_bytes: number;
        used_bytes: number;
        available_bytes: number;
        percentage_used: number;
      };
      error?: string;
    }> => {
      try {
        const options = await addAuthHeaders({
          method: 'POST'
        });
        
        const response = await fetch(`${API_BASE_URL}/storage/recalculate`, options);
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Failed to recalculate storage');
        }
        
        const data = await response.json();
        console.log('[DEBUG] Storage recalculation response:', data);
        
        return data;
      } catch (error) {
        console.error('[DEBUG] Error recalculating storage:', error);
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        };
      }
    }
  },

  subscriptions: {
    /**
     * Get available subscription plans
     */
    getPlans: async (): Promise<Plan[]> => {
      const options = await addAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/subscriptions/plans`, options);
      
      if (!response.ok) {
        throw new Error('Failed to fetch plans');
      }
      
      return response.json();
    },
    
    /**
     * Get current user's plan information
     */
    getMyPlan: async (): Promise<UserPlanInfo> => {
      const options = await addAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/subscriptions/my-plan`, options);
      
      if (!response.ok) {
        throw new Error('Failed to fetch plan information');
      }
      
      return response.json();
    },
    
    /**
     * Upgrade subscription plan
     */
    upgradePlan: async (planType: 'pro' | 'studio'): Promise<any> => {
      const options = await addAuthHeaders({
        method: 'POST',
        body: JSON.stringify({ plan_type: planType })
      });
      
      const response = await fetch(`${API_BASE_URL}/subscriptions/upgrade`, options);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upgrade plan');
      }
      
      return response.json();
    },
    
    /**
     * Downgrade subscription plan
     */
    downgradePlan: async (planType: 'free' | 'pro'): Promise<any> => {
      const options = await addAuthHeaders({
        method: 'POST',
        body: JSON.stringify({ plan_type: planType })
      });
      
      const response = await fetch(`${API_BASE_URL}/subscriptions/downgrade`, options);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to change plan');
      }
      
      return response.json();
    },
    
    /**
     * Get credit transaction history
     */
    getCreditHistory: async (limit: number = 50): Promise<any> => {
      const options = await addAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/subscriptions/credit-history?limit=${limit}`, options);
      
      if (!response.ok) {
        throw new Error('Failed to fetch credit history');
      }
      
      return response.json();
    },
    
    /**
     * Deduct credits when G-code files are accessed/viewed
     */
    deductCreditsForGcode: async (plateCount: number, jobId?: string): Promise<any> => {
      const options = await addAuthHeaders({
        method: 'POST',
        body: JSON.stringify({ 
          plate_count: plateCount,
          job_id: jobId || '',
          description: `G-code access - ${plateCount} plate${plateCount > 1 ? 's' : ''}`
        })
      });
      
      const response = await fetch(`${API_BASE_URL}/subscriptions/deduct-credits`, options);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to deduct credits');
      }
      
      return response.json();
    }
  },

  institution: {
    /**
     * Get current institution based on request context (subdomain, query param, etc.)
     */
    getCurrent: async (): Promise<InstitutionDetectionInfo> => {
      try {
        // Pass current URL parameters to the API call so the backend middleware can detect them
        const urlParams = new URLSearchParams(window.location.search);
        
        // Construct the API URL properly, handling both absolute and relative base URLs
        const baseUrl = API_BASE_URL.startsWith('http') ? API_BASE_URL : window.location.origin + API_BASE_URL;
        const apiUrl = new URL(`${baseUrl}/institution/current`);
        
        // Forward relevant parameters (especially 'institution' query param)
        if (urlParams.has('institution')) {
          apiUrl.searchParams.set('institution', urlParams.get('institution')!);
        }
        
        const options = await addAuthHeaders();
        const response = await fetch(apiUrl.toString(), options);
        
        if (!response.ok) {
          throw new Error(`Failed to get current institution: ${response.statusText}`);
        }
        
        return response.json();
      } catch (error) {
        console.error('Error getting current institution:', error);
        throw error;
      }
    },

    /**
     * Get public configuration for current institution (theme, branding)
     */
    getConfig: async (): Promise<InstitutionPublicConfig | null> => {
      try {
        const options = await addAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/institution/config`, options);
        
        if (!response.ok) {
          if (response.status === 404) {
            return null; // No institution found
          }
          throw new Error(`Failed to get institution config: ${response.statusText}`);
        }
        
        return response.json();
      } catch (error) {
        console.error('Error getting institution config:', error);
        throw error;
      }
    },

    /**
     * Get public configuration for a specific institution by subdomain
     */
    getConfigBySubdomain: async (subdomain: string): Promise<InstitutionPublicConfig> => {
      try {
        const options = await addAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/institution/config/${subdomain}`, options);
        
        if (!response.ok) {
          throw new Error(`Failed to get institution config for ${subdomain}: ${response.statusText}`);
        }
        
        return response.json();
      } catch (error) {
        console.error(`Error getting institution config for ${subdomain}:`, error);
        throw error;
      }
    },

    /**
     * Get list of available institutions (public subdomains)
     */
    getAvailable: async (): Promise<InstitutionPublicConfig[]> => {
      try {
        const options = await addAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/institution/available`, options);
        
        if (!response.ok) {
          throw new Error(`Failed to get available institutions: ${response.statusText}`);
        }
        
        const data = await response.json();
        return data.institutions || [];
      } catch (error) {
        console.error('Error getting available institutions:', error);
        throw error;
      }
    },

    /**
     * Test institution detection (debugging endpoint)
     */
    testDetection: async (): Promise<any> => {
      try {
        const options = await addAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/institution/test-detection`, options);
        
        if (!response.ok) {
          throw new Error(`Failed to test institution detection: ${response.statusText}`);
        }
        
        return response.json();
      } catch (error) {
        console.error('Error testing institution detection:', error);
        throw error;
      }
    },

    // Super Admin Institution Management
    /**
     * Get all institutions with full details (Super Admin only)
     */
    getAll: async (): Promise<Institution[]> => {
      try {
        const options = await addAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/institution/all`, options);
        
        if (!response.ok) {
          throw new Error(`Failed to get all institutions: ${response.statusText}`);
        }
        
        const data = await response.json();
        return data.institutions;
      } catch (error) {
        console.error('Error getting all institutions:', error);
        throw error;
      }
    },

    /**
     * Create a new institution (Super Admin only)
     */
    create: async (institutionData: Partial<Institution>): Promise<Institution> => {
      try {
        const options = await addAuthHeaders({
          method: 'POST',
          body: JSON.stringify(institutionData)
        });
        
        const response = await fetch(`${API_BASE_URL}/institution/`, options);
        
        if (!response.ok) {
          const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(error.detail || 'Failed to create institution');
        }
        
        const data = await response.json();
        return data.institution;
      } catch (error) {
        console.error('Error creating institution:', error);
        throw error;
      }
    },

    /**
     * Update an institution (Super Admin only)
     */
    update: async (institutionId: string, institutionData: Partial<Institution>): Promise<Institution> => {
      try {
        const options = await addAuthHeaders({
          method: 'PUT',
          body: JSON.stringify(institutionData)
        });
        
        const response = await fetch(`${API_BASE_URL}/institution/${institutionId}`, options);
        
        if (!response.ok) {
          const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(error.detail || 'Failed to update institution');
        }
        
        const data = await response.json();
        return data.institution;
      } catch (error) {
        console.error('Error updating institution:', error);
        throw error;
      }
    },

    /**
     * Delete an institution (Super Admin only)
     */
    delete: async (institutionId: string): Promise<void> => {
      try {
        const options = await addAuthHeaders({
          method: 'DELETE'
        });
        
        const response = await fetch(`${API_BASE_URL}/institution/${institutionId}`, options);
        
        if (!response.ok) {
          const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(error.detail || 'Failed to delete institution');
        }
      } catch (error) {
        console.error('Error deleting institution:', error);
        throw error;
      }
    },

    /**
     * Check if Google login is allowed for the current institution
     */
    checkGoogleLoginAllowed: async (): Promise<{ google_login_allowed: boolean; reason: string; institution_name?: string }> => {
      try {
        // Preserve query parameters from current URL for institution detection
        const currentUrlParams = new URLSearchParams(window.location.search);
        const institutionParam = currentUrlParams.get('institution');
        
        let url = `${API_BASE_URL}/institution/google-login-allowed`;
        if (institutionParam) {
          url += `?institution=${encodeURIComponent(institutionParam)}`;
        }
        
        const response = await fetch(url);
        
        if (!response.ok) {
          // If there's an error, default to allowing Google login
          return {
            google_login_allowed: true,
            reason: 'error_fallback'
          };
        }
        
        return response.json();
      } catch (error) {
        console.error('Error checking Google login permission:', error);
        // Default to allowing Google login in case of error
        return {
          google_login_allowed: true,
          reason: 'error_fallback'
        };
      }
    },

    /**
     * Validate if an email domain is allowed for the current institution
     */
    validateEmailDomain: async (email: string): Promise<{ valid: boolean; error?: string; institution_name?: string; allowed_domains?: string[] }> => {
      try {
        // Preserve query parameters from current URL for institution detection
        const currentUrlParams = new URLSearchParams(window.location.search);
        const institutionParam = currentUrlParams.get('institution');
        
        let url = `${API_BASE_URL}/institution/validate-email-domain`;
        if (institutionParam) {
          url += `?institution=${encodeURIComponent(institutionParam)}`;
        }
        
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email })
        });
        
        if (!response.ok) {
          return {
            valid: false,
            error: 'Error validating email domain'
          };
        }
        
        return response.json();
      } catch (error) {
        console.error('Error validating email domain:', error);
        return {
          valid: false,
          error: 'Error validating email domain'
        };
      }
    },

    /**
     * Get institution statistics (Super Admin only)
     */
    getStats: async (): Promise<any> => {
      try {
        const options = await addAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/institution/stats`, options);
        
        if (!response.ok) {
          throw new Error(`Failed to get institution stats: ${response.statusText}`);
        }
        
        const data = await response.json();
        return data.stats;
      } catch (error) {
        console.error('Error getting institution stats:', error);
        throw error;
      }
    },

    /**
     * Upload a logo file for an institution (Super Admin only)
     */
    uploadLogo: async (file: File): Promise<{ success: boolean; logo_url: string; message: string }> => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const token = await getAuthToken();
        const headers: HeadersInit = {};
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`${API_BASE_URL}/institution/upload-logo`, {
          method: 'POST',
          headers,
          body: formData,
          credentials: 'include'
        });
        
        if (!response.ok) {
          const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(error.detail || 'Failed to upload logo');
        }
        
        return response.json();
      } catch (error) {
        console.error('Error uploading logo:', error);
        throw error;
      }
    }
  },

  licenses: {
    /**
     * Get all license definitions (Super Admin only)
     */
    getAll: async (includeInactive: boolean = false): Promise<LicenseDefinition[]> => {
      try {
        const options = await addAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/licenses/?include_inactive=${includeInactive}`, options);
        
        if (!response.ok) {
          const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(error.detail || 'Failed to get licenses');
        }
        
        return response.json();
      } catch (error) {
        console.error('Error getting licenses:', error);
        throw error;
      }
    },

    /**
     * Get a specific license by ID
     */
    get: async (licenseId: string): Promise<LicenseDefinition> => {
      try {
        const options = await addAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/licenses/${licenseId}`, options);
        
        if (!response.ok) {
          const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(error.detail || 'Failed to get license');
        }
        
        return response.json();
      } catch (error) {
        console.error(`Error getting license ${licenseId}:`, error);
        throw error;
      }
    },

    /**
     * Update a license (Super Admin only)
     */
    update: async (licenseId: string, updates: Partial<LicenseDefinition>): Promise<LicenseDefinition> => {
      try {
        const options = await addAuthHeaders({
          method: 'PUT',
          body: JSON.stringify(updates)
        });
        
        const response = await fetch(`${API_BASE_URL}/licenses/${licenseId}`, options);
        
        if (!response.ok) {
          const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(error.detail || 'Failed to update license');
        }
        
        return response.json();
      } catch (error) {
        console.error(`Error updating license ${licenseId}:`, error);
        throw error;
      }
    },

    /**
     * Create a new custom license (Super Admin only)
     */
    create: async (licenseData: LicenseCreate): Promise<LicenseDefinition> => {
      try {
        const options = await addAuthHeaders({
          method: 'POST',
          body: JSON.stringify(licenseData)
        });
        
        const response = await fetch(`${API_BASE_URL}/licenses/`, options);
        
        if (!response.ok) {
          const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(error.detail || 'Failed to create license');
        }
        
        return response.json();
      } catch (error) {
        console.error('Error creating license:', error);
        throw error;
      }
    },

    /**
     * Initialize default licenses (Super Admin only)
     */
    initialize: async (): Promise<any> => {
      try {
        const options = await addAuthHeaders({
          method: 'POST'
        });
        
        const response = await fetch(`${API_BASE_URL}/licenses/initialize`, options);
        
        if (!response.ok) {
          const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(error.detail || 'Failed to initialize licenses');
        }
        
        return response.json();
      } catch (error) {
        console.error('Error initializing licenses:', error);
        throw error;
      }
    },

    /**
     * Get license analytics and summary
     */
    getSummary: async (): Promise<any> => {
      try {
        const options = await addAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/licenses/analytics/summary`, options);
        
        if (!response.ok) {
          const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(error.detail || 'Failed to get license summary');
        }
        
        return response.json();
      } catch (error) {
        console.error('Error getting license summary:', error);
        throw error;
      }
    },

    /**
     * Get license comparison data
     */
    getComparison: async (): Promise<any> => {
      try {
        const options = await addAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/licenses/analytics/comparison`, options);
        
        if (!response.ok) {
          const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(error.detail || 'Failed to get license comparison');
        }
        
        return response.json();
      } catch (error) {
        console.error('Error getting license comparison:', error);
        throw error;
      }
    },

    /**
     * Get licenses by billing model
     */
    getByBillingModel: async (billingModel: 'hours_based' | 'pay_per_print'): Promise<any> => {
      try {
        const options = await addAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/licenses/filter/by-billing-model/${billingModel}`, options);
        
        if (!response.ok) {
          const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
          throw new Error(error.detail || 'Failed to filter licenses');
        }
        
        return response.json();
      } catch (error) {
        console.error(`Error getting licenses by billing model ${billingModel}:`, error);
        throw error;
      }
    }
  }
}; 